import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { logActivity } from '../utils/activityLogger';
import { 
  canPerformAction, 
  getRoleDisplayName, 
  getPermissionDisplayName,
  getPermissionsByCategory,
  grantPermissionAPI,
  revokePermissionAPI,
  hasPermission,
  getVoicePartDisplay
} from '../utils/permissionUtils';
import UserProfile from './UserProfile';
import '../styles/manage-users.css';

const ManageUsers = ({ isOpen, onClose }) => {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'visitor', 'member', 'admin', 'super-admin'
  const [pendingPermissions, setPendingPermissions] = useState(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Check if current user can manage users
  const canManageUsers = hasPermission(currentUser, 'manage-users');
  const canManagePermissions = canPerformAction(currentUser, 'grant-permission');

  useEffect(() => {
    if (isOpen && canManageUsers) {
      loadUsers();
      loadPermissions();
    }
  }, [isOpen, canManageUsers]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const url = `${API_BASE}/api/permissions?operation=listUsers&userId=${currentUser.id}`;
      // console.log('Loading users from:', url);
      // console.log('Current user:', currentUser);
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        const errorData = await response.json();
        console.error('Failed to load users:', response.status, errorData);
        throw new Error(`Failed to load users: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      alert(t('userManagement.failedToLoadUsers'));
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/permissions?operation=listPermissions&userId=${currentUser.id}&t=${Date.now()}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setPermissions(data.permissions || []);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!canManagePermissions) return;
    
    const targetUser = users.find(u => u.id === userId);
    const oldRole = targetUser?.role;
    
    logActivity('user_role_change', 'user', userId, 
      `Admin changed user ${targetUser?.name || userId} role from ${oldRole} to ${newRole}`,
      { targetUserId: userId, targetUserName: targetUser?.name, oldRole, newRole });
    
    setActionLoading(true);
    try {
      const result = await grantPermissionAPI(currentUser.id, userId, newRole);
      if (result.success) {
        await loadUsers(); // Refresh the user list
        alert(t('userManagement.roleUpdated'));
      } else {
        alert(t('userManagement.roleUpdateFailed', { error: result.error }));
      }
    } catch (error) {
      console.error('Error updating role:', error);
      alert(t('userManagement.roleUpdateError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePermissionToggle = (permissionName) => {
    if (!canManagePermissions) return;
    
    const newPendingPermissions = new Set(pendingPermissions);
    
    if (newPendingPermissions.has(permissionName)) {
      newPendingPermissions.delete(permissionName);
    } else {
      newPendingPermissions.add(permissionName);
    }
    
    setPendingPermissions(newPendingPermissions);
    setHasChanges(true);
  };

  const applyPermissionChanges = async () => {
    if (!selectedUser || !hasChanges) return;
    
    setActionLoading(true);
    try {
      const currentPermissions = new Set(selectedUser.permissions || []);
      const desiredPermissions = new Set(pendingPermissions);
      
      // Find permissions to grant and revoke
      const permissionsToGrant = [...desiredPermissions].filter(p => !currentPermissions.has(p));
      const permissionsToRevoke = [...currentPermissions].filter(p => !desiredPermissions.has(p));
      
      // Grant new permissions
      for (const permission of permissionsToGrant) {
        logActivity('permission_grant', 'user', selectedUser.id, 
          `Admin granted ${permission} permission to user ${selectedUser.name || selectedUser.id}`,
          { targetUserId: selectedUser.id, targetUserName: selectedUser.name, permission });
        const result = await grantPermissionAPI(currentUser.id, selectedUser.id, permission);
        if (!result.success) {
          throw new Error(`Failed to grant ${permission}: ${result.error}`);
        }
      }
      
      // Revoke removed permissions
      for (const permission of permissionsToRevoke) {
        logActivity('permission_revoke', 'user', selectedUser.id, 
          `Admin revoked ${permission} permission from user ${selectedUser.name || selectedUser.id}`,
          { targetUserId: selectedUser.id, targetUserName: selectedUser.name, permission });
        const result = await revokePermissionAPI(currentUser.id, selectedUser.id, permission);
        if (!result.success) {
          throw new Error(`Failed to revoke ${permission}: ${result.error}`);
        }
      }
      
      // Refresh the user list and close modal
      await loadUsers();
      setPendingPermissions(new Set());
      setHasChanges(false);
      setShowPermissionModal(false);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('Error applying permission changes:', error);
      alert(t('userManagement.applyChangesFailed', { error: error.message }));
    } finally {
      setActionLoading(false);
    }
  };

  const cancelPermissionChanges = () => {
    setPendingPermissions(new Set());
    setHasChanges(false);
    setShowPermissionModal(false);
    setSelectedUser(null);
  };

  const isPermissionChecked = (permissionName) => {
    return pendingPermissions.has(permissionName);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.display_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' || user.role === filter;
    
    return matchesSearch && matchesFilter;
  });

  const openPermissionModal = (user) => {
    setSelectedUser(user);
    setPendingPermissions(new Set(user.permissions || []));
    setHasChanges(false);
    setShowPermissionModal(true);
  };

  const closePermissionModal = () => {
    if (hasChanges) {
      if (window.confirm(t('userManagement.unsavedChanges'))) {
        cancelPermissionChanges();
      }
    } else {
      cancelPermissionChanges();
    }
  };

  const openProfileEditor = (user) => {
    setEditingUser(user);
    setShowProfileEditor(true);
  };

  const closeProfileEditor = () => {
    setShowProfileEditor(false);
    setEditingUser(null);
  };

  const handleProfileUpdate = async () => {
    // Refresh the user list to show updated profile data
    await loadUsers();
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setActionLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/user?operation=deleteUser&userId=${currentUser.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetUserId: userToDelete.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        await loadUsers(); // Refresh the user list
        setShowDeleteConfirm(false);
        setUserToDelete(null);
        alert(t('userManagement.userDeleted', { user: userToDelete.display_name || userToDelete.email }));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(t('userManagement.deleteUserError', { error: error.message }));
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  if (!isOpen) return null;

  if (!canManageUsers) {
    return (
      <div className="manage-users-overlay">
        <div className="manage-users-modal">
          <div className="manage-users-header">
            <h2>{t('userManagement.accessDenied')}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="manage-users-content">
            <p>{t('userManagement.noPermission')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-users-overlay">
      <div className="manage-users-modal">
        <div className="manage-users-header">
          <h2>{t('userManagement.title')}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="manage-users-content">
          {/* Search and Filter Controls */}
          <div className="users-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder={t('userManagement.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => {
                  const newSearchTerm = e.target.value;
                  if (newSearchTerm !== searchTerm) {
                    logActivity('user_search', 'user', null, 
                      `Admin searched for users with term: ${newSearchTerm}`,
                      { searchTerm: newSearchTerm, resultCount: filteredUsers.length });
                  }
                  setSearchTerm(newSearchTerm);
                }}
                className="search-input"
              />
            </div>
            <div className="filter-controls">
              <label htmlFor="role-filter">{t('userManagement.filterByRole')}</label>
              <select
                id="role-filter"
                value={filter}
                onChange={(e) => {
                  const newFilter = e.target.value;
                  logActivity('user_filter', 'user', null, 
                    `Admin filtered users by role: ${newFilter}`,
                    { filter: newFilter, resultCount: filteredUsers.length });
                  setFilter(newFilter);
                }}
                className="filter-select"
              >
                <option value="all">{t('userManagement.allRoles')}</option>
                <option value="visitor">{t('userManagement.visitors')}</option>
                <option value="member">{t('userManagement.members')}</option>
                <option value="admin">{t('userManagement.admins')}</option>
                <option value="super-admin">{t('userManagement.superAdmins')}</option>
              </select>
            </div>
          </div>

          {/* Users List */}
          {loading ? (
            <div className="loading-state">{t('userManagement.loadingUsers')}</div>
          ) : (
            <div className="users-list">
              {filteredUsers.length === 0 ? (
                <div className="empty-state">
                  {searchTerm || filter !== 'all' ? t('userManagement.noMatchingUsers') : t('userManagement.noUsers')}
                </div>
              ) : (
                filteredUsers.map(user => (
                  <div key={user.id} className="user-card">
                    <div className="user-info">
                      <div className="user-avatar">
                        {user.display_name ? user.display_name.charAt(0).toUpperCase() : '?'}
                      </div>
                      <div className="user-details">
                        <div className="user-primary">
                          <span className="user-name">{user.display_name || t('userManagement.noName')}</span>
                          <span className="user-email">({user.email})</span>
                        </div>
                        <div className="user-meta">
                          <div className="user-badges">
                            <span className={`role-badge role-${user.role}`}>
                              {getRoleDisplayName(user.role)}
                            </span>
                            {user.voice_part && (
                              <span className="voice-part-badge" style={{color: getVoicePartDisplay(user.voice_part).color}}>
                                {getVoicePartDisplay(user.voice_part).icon} {getVoicePartDisplay(user.voice_part).name}
                              </span>
                            )}
                          </div>
                          {user.bio && (
                            <div className="user-bio">
                              {user.bio.length > 80 ? `${user.bio.substring(0, 80)}...` : user.bio}
                            </div>
                          )}
                          <div className="user-dates">
                            <span className="user-date">
                              {t('userManagement.joined', { date: new Date(user.created_at).toLocaleDateString() })}
                            </span>
                            {user.last_login && (
                              <span className="user-last-login">
                                {t('userManagement.lastLogin', { 
                                  date: new Date(user.last_login).toLocaleString()
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="user-actions">
                      {canManageUsers && (
                        <>
                          <button
                            onClick={() => openProfileEditor(user)}
                            disabled={actionLoading}
                            className="edit-profile-button"
                            title={t('userManagement.editProfile')}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M3,17.25V21H6.75L17.81,9.94L14.06,6.19L3,17.25M20.71,7.04A1,1 0 0,0 20.71,5.63L18.37,3.29A1,1 0 0,0 17,3.29A1,1 0 0,0 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04Z" />
                            </svg>
                            {t('userManagement.editProfile')}
                          </button>
                          {user.id !== currentUser.id && (user.role !== 'super-admin' || currentUser.role === 'super-admin') && (
                            <button
                              onClick={() => confirmDeleteUser(user)}
                              disabled={actionLoading}
                              className="delete-user-button"
                              title={t('userManagement.deleteUser')}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                              </svg>
                              {t('userManagement.deleteUser')}
                            </button>
                          )}
                        </>
                      )}
                      {canManagePermissions && (
                        <>
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            disabled={actionLoading}
                            className="role-select"
                          >
                            <option value="visitor">{t('userManagement.roleVisitor')}</option>
                            <option value="member">{t('userManagement.roleMember')}</option>
                            <option value="admin">{t('userManagement.roleAdmin')}</option>
                            {currentUser.role === 'super-admin' && (
                              <option value="super-admin">{t('userManagement.roleSuperAdmin')}</option>
                            )}
                          </select>
                          
                          <button
                            onClick={() => openPermissionModal(user)}
                            disabled={actionLoading}
                            className="permissions-button"
                          >
                            {t('userManagement.permissions', { count: user.permissions?.length || 0 })}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Permission Management Modal */}
      {showPermissionModal && selectedUser && (
        <div className="permission-modal-overlay">
          <div className="permission-modal">
            <div className="permission-modal-header">
              <h3>{t('userManagement.managePermissions', { user: selectedUser.display_name || selectedUser.email })}</h3>
              <button className="close-button" onClick={closePermissionModal}>×</button>
            </div>
            
            <div className="permission-modal-content">
              <div className="current-role">
                <strong>{t('userManagement.currentRole')}</strong> 
                <span className={`role-badge role-${selectedUser.role}`}>
                  {getRoleDisplayName(selectedUser.role)}
                </span>
              </div>

              <div className="permissions-grid">
                {Object.entries(getPermissionsByCategory()).map(([categoryKey, category]) => (
                  <div key={categoryKey} className="permission-category">
                    <h4 className="category-title">{category.name}</h4>
                    <div className="permission-items">
                      {category.permissions.map(permissionName => {
                        const isChecked = isPermissionChecked(permissionName);
                        return (
                          <div key={permissionName} className="permission-item">
                            <label className="permission-label">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handlePermissionToggle(permissionName)}
                                disabled={actionLoading}
                                className="permission-checkbox"
                              />
                              <span className="permission-name">
                                {getPermissionDisplayName(permissionName)}
                              </span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="permission-modal-actions">
                <button 
                  onClick={cancelPermissionChanges}
                  className="cancel-button"
                  disabled={actionLoading}
                >
                  {t('controls.cancel')}
                </button>
                <button 
                  onClick={applyPermissionChanges}
                  className={`apply-button ${hasChanges ? 'has-changes' : ''}`}
                  disabled={actionLoading || !hasChanges}
                >
                  {actionLoading ? t('userManagement.applying') : t('userManagement.applyChanges')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Profile Editor Modal */}
      {showProfileEditor && editingUser && (
        <UserProfile 
          isOpen={showProfileEditor} 
          onClose={closeProfileEditor}
          onProfileUpdate={handleProfileUpdate}
          editingUserId={editingUser.id}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="permission-modal-overlay">
          <div className="permission-modal delete-confirm-modal">
            <div className="permission-modal-header">
              <h3>{t('userManagement.confirmDelete')}</h3>
              <button className="close-button" onClick={cancelDeleteUser}>×</button>
            </div>
            
            <div className="permission-modal-content">
              <div className="delete-warning">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="#ff4444" className="warning-icon">
                  <path d="M12,2C13.1,2 14,2.9 14,4C14,5.1 13.1,6 12,6C10.9,6 10,5.1 10,4C10,2.9 10.9,2 12,2M21,9V7L15,1H5C3.89,1 3,1.89 3,3V21A2,2 0 0,0 5,23H19A2,2 0 0,0 21,21V9M19,9H14V4L19,9Z" />
                </svg>
                <p>
                  {t('userManagement.deleteConfirmMessage', { 
                    user: userToDelete.display_name || userToDelete.email 
                  })}
                </p>
                <p className="delete-warning-note">
                  {t('userManagement.deleteWarning')}
                </p>
              </div>
              
              <div className="user-info-preview">
                <div className="user-avatar">
                  {userToDelete.display_name ? userToDelete.display_name.charAt(0).toUpperCase() : '?'}
                </div>
                <div className="user-details">
                  <div className="user-name">{userToDelete.display_name || t('userManagement.noName')}</div>
                  <div className="user-email">{userToDelete.email}</div>
                  <span className={`role-badge role-${userToDelete.role}`}>
                    {getRoleDisplayName(userToDelete.role)}
                  </span>
                </div>
              </div>
              
              <div className="permission-modal-actions">
                <button 
                  onClick={cancelDeleteUser}
                  className="cancel-button"
                  disabled={actionLoading}
                >
                  {t('controls.cancel')}
                </button>
                <button 
                  onClick={handleDeleteUser}
                  className="delete-button"
                  disabled={actionLoading}
                >
                  {actionLoading ? t('userManagement.deleting') : t('userManagement.confirmDeleteButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;